import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw, RouterScrollBehavior } from "vue-router";
import { nextTick } from "vue";
import { authGuard } from "./auth-guard";
import { getLogger } from "@/utils/logger";
import { focusRegisteredPageHeading } from "@/core/navigation/pageHeadingRegistry";
import { adminBreadcrumb, type BreadcrumbItem } from "@/navigation/breadcrumbs";
import { APP_BASE_URL, appHref } from "@/utils/appBase";

// Breadcrumb-meta helper for club-scoped leaf routes that are SIBLINGS of a club
// nav item ([NAV-BREADCRUMB-DECLARED-PARENT]). `parentKey` is a club sub-item key (e.g.
// "meetings"); the resolver matches `club:<shortName>:<parentKey>` against the
// district-prefixed node id, so the district need not be known here.
function clubLeafBreadcrumb(parentKey: string, tail: BreadcrumbItem[]) {
  return {
    parentNodeId: (route: { params: Record<string, unknown> }) =>
      `club:${String(route.params.clubShortName)}:${parentKey}`,
    tail: () => tail,
  };
}

// Breadcrumb-meta helper for district-scoped leaf routes (upload complete,
// dashboard sub-pages). `parentKey` is a district child key or "" for the
// district node itself. These routes are only reachable by district managers,
// so the officer-gated navModel node is guaranteed to exist.
function districtLeafBreadcrumb(parentKey: string, tail: BreadcrumbItem[]) {
  return {
    parentNodeId: (route: { params: Record<string, unknown> }) => {
      const districtId = String(route.params.districtId);
      return parentKey ? `district:${districtId}:${parentKey}` : `district:${districtId}`;
    },
    tail: () => tail,
  };
}

// Breadcrumb-meta helper for the COT routes. Some (event detail / register) are
// user-facing and reachable by officers who are NOT district managers, for whom
// the officer-gated `district:<id>:cot` navModel node does not exist. So carry a
// route-derived `District N > COT` static base too: the resolver prefers the
// navModel chain when the node exists (district managers) and falls back to the
// static base otherwise, so every persona keeps a trail (issue #2477 review).
function cotLeafBreadcrumb(tail: BreadcrumbItem[]) {
  return {
    parentNodeId: (route: { params: Record<string, unknown> }) => `district:${String(route.params.districtId)}:cot`,
    staticBase: (route: { params: Record<string, unknown> }): BreadcrumbItem[] => {
      const districtId = String(route.params.districtId);
      return [
        { label: `District ${districtId}`, to: `/dashboard/district/${districtId}` },
        { label: "COT", to: `/district/${districtId}/cot` },
      ];
    },
    tail: () => tail,
  };
}

const logger = getLogger("Router");

// Layouts are still imported directly as they're essential and small
import MainLayout from "@/layouts/MainLayout.vue";
import ChatLayout from "@/chat/layouts/ChatLayout.vue";
import GuestLayout from "@/club/layouts/GuestLayout.vue";

const CHUNK_LOAD_ERROR_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk \d+ failed/i;
const CHUNK_LOAD_RELOAD_KEY = "toasthost:chunk-load-reloaded";
const CHUNK_RELOAD_QUERY_PARAM = "__chunkReload";
const CHUNK_HARD_FAIL_QUERY_PARAM = "__chunkReloadHardFail";
let hasShownChunkHardFailAlert = false;

// Use dynamic imports for all views to enable code-splitting
export const routes: RouteRecordRaw[] = [
  {
    path: "/",
    component: MainLayout,
    children: [
      {
        path: "",
        component: () => import(/* webpackChunkName: "home" */ "@/views/PublicHome.vue"),
        meta: { public: true, redirectAuthenticatedTo: "/home" },
      },
      {
        path: "home",
        component: () =>
          import(/* webpackChunkName: "dashboard-layout" */ "@/dashboard/layouts/DashboardWorkspaceLayout.vue"),
        meta: { requiresAuth: true },
        children: [
          {
            path: "",
            name: "UserHome",
            component: () => import(/* webpackChunkName: "user-home" */ "@/views/UserHome.vue"),
          },
          {
            // Officer COT now lives inline as a collapsible section on the home
            // page (HomeCOTSection). Redirect old /app/home/cot bookmarks home.
            path: "cot",
            redirect: { name: "UserHome" },
          },
        ],
      },
      {
        // The pathway catalogue is reference data, not club data: no club in the
        // path and no officer gate, so the member who owns a speech can read the
        // project it is credited to. Matches either a pathway project id
        // ("311:8101") or an education-series one ("EDU::..."), both of which
        // vue-router round-trips through a path segment unencoded.
        path: "pathways/project/:projectId",
        name: "PathwayProject",
        component: () => import(/* webpackChunkName: "pathway-project" */ "@/pathway/views/PathwayProjectPage.vue"),
      },
      {
        path: "/club/:clubShortName/meeting", // Matches the backend redirect target
        name: "ClubMeetingRouter",
        component: () =>
          import(/* webpackChunkName: "club-meeting-router" */ "@/club/views/clubmeetings/ClubMeetingRouter.vue"),
        props: true, // Pass route params (clubShortName) as props
        meta: { public: true, remountOnParamChange: true, remountAtAppShell: true },
      },
      {
        path: "club/:clubShortName/meeting/:meetingId",
        name: "ClubMeetingView",
        component: () => import(/* webpackChunkName: "meeting-core" */ "@/meeting-core/views/MeetingView.vue"),
        props: true,
        // Meeting detail is a SIBLING of the club "Meetings" nav item, so declare
        // its parent explicitly. The leaf label (meeting title) is published at
        // runtime via useBreadcrumbTail; "Meeting" is the pre-load fallback.
        meta: {
          public: true,
          remountOnParamChange: true,
          remountAtAppShell: true,
          breadcrumb: clubLeafBreadcrumb("meetings", [{ label: "Meeting" }]),
        },
      },
      {
        path: "club/:clubShortName/meeting/:meetingId/print",
        name: "ClubMeetingPrintView",
        component: () =>
          import(/* webpackChunkName: "meeting-core" */ "@/meeting-core/views/MeetingAgendaPrintView.vue"),
        props: true,
        meta: { public: true, remountOnParamChange: true, remountAtAppShell: true },
      },
      {
        path: "login/",
        component: () => import(/* webpackChunkName: "auth" */ "@/views/LoginPage.vue"),
        meta: { public: true },
      },
      {
        path: "signup/",
        component: () => import(/* webpackChunkName: "auth" */ "@/views/SignUp.vue"),
        meta: { public: true },
      },
      {
        path: "forgot-password/",
        component: () => import(/* webpackChunkName: "auth" */ "@/views/ForgotPassword.vue"),
        meta: { public: true },
      },
      {
        path: "reset-password/:token",
        component: () => import(/* webpackChunkName: "auth" */ "@/views/ResetPassword.vue"),
        props: true,
        meta: { public: true },
      },
      {
        path: "activate/:token",
        component: () => import(/* webpackChunkName: "auth" */ "@/views/ActivateAccount.vue"),
        props: true,
        meta: { public: true },
      },
      {
        path: "guest-verify/:token",
        component: () => import(/* webpackChunkName: "guest" */ "@/views/GuestVerify.vue"),
        props: true,
        meta: { public: true },
      },
      {
        path: "profile/",
        component: () => import(/* webpackChunkName: "profile" */ "@/views/UserProfile.vue"),
      },
      {
        path: "complete-profile",
        component: () => import(/* webpackChunkName: "profile" */ "@/views/ProfileCompletion.vue"),
        meta: { public: true },
      },
      {
        // The FAQ now lives on the documentation site. Hand off existing
        // /app/faq bookmarks, links, and search results to the canonical docs
        // FAQ instead of letting them fall through to the catch-all → /app/.
        path: "faq/",
        component: { render: () => null },
        beforeEnter: () => {
          window.location.replace("https://docs.toasthost.co.uk/reference/faq/");
          return false;
        },
        meta: { public: true },
      },
      {
        path: "privacy/",
        component: () => import(/* webpackChunkName: "pages" */ "@/views/pages/PrivacyPage.vue"),
        meta: { public: true },
      },
      {
        path: "clubs/find",
        component: () => import(/* webpackChunkName: "club-find" */ "@/club/views/FindClub.vue"),
        meta: { public: true },
      },
      {
        path: "club/:clubShortName",
        component: () => import(/* webpackChunkName: "club-workspace" */ "@/club/layouts/ClubWorkspaceLayout.vue"),
        props: true,
        children: [
          {
            path: "",
            name: "ClubHome",
            component: () => import(/* webpackChunkName: "club-home" */ "@/club/views/ClubHome.vue"),
            props: true,
            meta: { public: true, remountOnParamChange: true },
          },
          {
            path: "dashboard",
            name: "ClubDCPDashboard",
            component: () => import(/* webpackChunkName: "club-dcp-dashboard" */ "@/club/views/ClubDCPDashboard.vue"),
            props: true,
            meta: { public: true, remountOnParamChange: true },
          },
          {
            path: "meetings",
            name: "ClubMeetings",
            component: () => import(/* webpackChunkName: "meetings" */ "@/club/views/clubmeetings/ClubMeetings.vue"),
            props: true,
            meta: { public: true, remountOnParamChange: true },
          },
          {
            path: "education",
            name: "ClubEducation",
            component: () => import(/* webpackChunkName: "club-education" */ "@/club/views/ClubEducation.vue"),
            props: true,
            meta: { remountOnParamChange: true },
          },
          {
            path: "education/activity",
            name: "ClubMemberActivity",
            component: () =>
              import(/* webpackChunkName: "club-member-activity" */ "@/club/views/ClubMemberActivity.vue"),
            props: true,
            meta: {
              remountOnParamChange: true,
              breadcrumb: clubLeafBreadcrumb("education", [{ label: "Member Activity" }]),
            },
          },
          {
            path: "education/roles",
            name: "ClubRoleHistory",
            component: () => import(/* webpackChunkName: "club-role-history" */ "@/club/views/ClubRoleHistory.vue"),
            props: true,
            meta: {
              remountOnParamChange: true,
              breadcrumb: clubLeafBreadcrumb("education", [{ label: "Role History" }]),
            },
          },
          {
            path: "membership",
            name: "ClubMembership",
            component: () => import(/* webpackChunkName: "membership" */ "@/club/views/ClubMembership.vue"),
            props: true,
            meta: { remountOnParamChange: true },
          },
          {
            path: "guestbookManagement",
            name: "GuestbookManagement",
            component: () => import(/* webpackChunkName: "guestbook" */ "@/club/views/GuestbookManagement.vue"),
            props: true,
            meta: { remountOnParamChange: true },
          },
          {
            path: "websiteConfig",
            name: "WebsiteConfig",
            component: () => import(/* webpackChunkName: "club-config" */ "@/club/views/WebsiteConfig.vue"),
            props: true,
            meta: { remountOnParamChange: true },
          },
          {
            path: "settings",
            name: "ClubSettings",
            component: () => import(/* webpackChunkName: "club-config" */ "@/club/views/ClubSettings.vue"),
            props: true,
            meta: { remountOnParamChange: true },
          },
          {
            path: "periodicEmailPreview",
            name: "PeriodicEmailPreview",
            component: () => import(/* webpackChunkName: "club-config" */ "@/club/views/PeriodicEmailPreview.vue"),
            props: true,
            meta: {
              remountOnParamChange: true,
              breadcrumb: clubLeafBreadcrumb("settings", [{ label: "Periodic Email Preview" }]),
            },
          },
          {
            path: "finance",
            name: "ClubFinance",
            component: () => import(/* webpackChunkName: "club-finance" */ "@/club/views/ClubFinance.vue"),
            props: true,
            meta: { remountOnParamChange: true },
          },
          {
            path: "subscription",
            name: "ClubSubscription",
            component: () => import(/* webpackChunkName: "club-subscription" */ "@/club/views/ClubSubscription.vue"),
            props: true,
            meta: { remountOnParamChange: true },
          },
          {
            path: "easyspeak-import",
            name: "EasySpeakImportReview",
            component: () =>
              import(
                /* webpackChunkName: "easyspeak-import" */ "@/components/club/easyspeak/EasySpeakUserMappingReview.vue"
              ),
            props: true,
            meta: {
              requiresAuth: true,
              requiresOfficer: true,
              remountOnParamChange: true,
              breadcrumb: clubLeafBreadcrumb("education", [{ label: "EasySpeak Import" }]),
            },
          },
          {
            path: "easyspeak-import/meetings",
            name: "EasySpeakMeetingsBrowser",
            component: () =>
              import(
                /* webpackChunkName: "easyspeak-import" */ "@/components/club/easyspeak/EasySpeakMeetingsBrowser.vue"
              ),
            props: true,
            meta: {
              requiresAuth: true,
              requiresOfficer: true,
              remountOnParamChange: true,
              // A named location with no params takes the current route's club, so the middle crumb
              // links to this club's review.
              breadcrumb: clubLeafBreadcrumb("education", [
                { label: "EasySpeak Import", to: { name: "EasySpeakImportReview" } },
                { label: "Meetings" },
              ]),
            },
          },
        ],
      },
      {
        path: "club/:clubShortName/guestbookEmail",
        component: () => import(/* webpackChunkName: "guestbook" */ "@/club/views/GuestbookEmail.vue"),
        props: true,
        meta: {
          remountOnParamChange: true,
          remountAtAppShell: true,
          breadcrumb: clubLeafBreadcrumb("guestbook", [{ label: "Email" }]),
        },
      },
      {
        path: "club/:clubShortName/membership/connect",
        component: () => import(/* webpackChunkName: "membership" */ "@/club/views/ClubStripeConnect.vue"),
        props: true,
        meta: { remountOnParamChange: true, remountAtAppShell: true },
      },
      {
        path: "club/:clubShortName/membership/testPayment",
        component: () => import(/* webpackChunkName: "membership" */ "@/club/views/TestPayment.vue"),
        props: true,
        meta: { remountOnParamChange: true, remountAtAppShell: true },
      },
      {
        path: "club/:clubShortName/membership/newMember",
        component: () => import(/* webpackChunkName: "membership" */ "@/club/views/NewMemberForm.vue"),
        props: true,
        meta: { public: true, remountOnParamChange: true, remountAtAppShell: true },
      },
      {
        path: "club/:clubShortName/membership/makePayment/:actionId",
        component: () => import(/* webpackChunkName: "membership" */ "@/club/views/MembershipPayment.vue"),
        props: true,
        meta: { public: true, remountOnParamChange: true, remountAtAppShell: true },
      },
      {
        path: "club/:clubShortName/membership/notRenewing/:actionId",
        component: () => import(/* webpackChunkName: "membership" */ "@/club/views/MembershipNotRenewing.vue"),
        props: true,
        meta: { public: true, remountOnParamChange: true, remountAtAppShell: true },
      },
      {
        path: "club/:clubShortName/membership/markAsPaid/:actionId",
        component: () => import(/* webpackChunkName: "membership" */ "@/club/views/MembershipAlreadyPaid.vue"),
        props: true,
        meta: { public: true, remountOnParamChange: true, remountAtAppShell: true },
      },
      {
        path: "club/guestUnsubscribe/:removeId",
        component: () => import(/* webpackChunkName: "guest" */ "@/club/views/GuestUnsubscribe.vue"),
        props: true,
        meta: { public: true, remountOnParamChange: true, remountAtAppShell: true },
      },
      {
        path: "proposal/:token",
        name: "RoleProposalContext",
        component: () => import(/* webpackChunkName: "proposal" */ "@/views/proposal/RoleProposalContext.vue"),
        props: true,
        meta: { public: true },
      },
      {
        path: "proposal/:token/result",
        name: "RoleProposalResponse",
        component: () => import(/* webpackChunkName: "proposal" */ "@/views/proposal/RoleProposalResponse.vue"),
        props: true,
        meta: { public: true },
      },
      // Find-a-club / dashboard overview
      {
        path: "findaclub",
        component: () =>
          import(/* webpackChunkName: "dashboard-layout" */ "@/dashboard/layouts/DashboardWorkspaceLayout.vue"),
        children: [
          {
            path: "",
            name: "FindAClub",
            component: () => import(/* webpackChunkName: "dashboard" */ "@/dashboard/views/DashboardOverview.vue"),
            meta: { public: true },
          },
        ],
      },
      // Legacy redirect from /app/dashboard to /app/findaclub
      {
        path: "dashboard",
        redirect: "/findaclub",
      },
      {
        path: "dashboard/district/:districtId",
        component: () =>
          import(/* webpackChunkName: "dashboard-layout" */ "@/dashboard/layouts/DashboardWorkspaceLayout.vue"),
        props: true,
        children: [
          {
            path: "",
            name: "DistrictDashboard",
            component: () => import(/* webpackChunkName: "dashboard" */ "@/dashboard/views/DistrictDashboard.vue"),
            props: true,
            meta: { public: true, remountOnParamChange: true },
          },
          {
            path: "download",
            component: () => import(/* webpackChunkName: "dashboard" */ "@/dashboard/views/DistrictDataDownload.vue"),
            props: true,
            meta: { remountOnParamChange: true, breadcrumb: districtLeafBreadcrumb("", [{ label: "Download Data" }]) },
          },
          {
            path: "clubsDashboard",
            component: () => import(/* webpackChunkName: "dashboard" */ "@/dashboard/views/ClubsDashboard.vue"),
            props: true,
            meta: {
              remountOnParamChange: true,
              breadcrumb: districtLeafBreadcrumb("", [{ label: "Clubs Dashboard" }]),
            },
          },
          {
            path: "dcpOverview",
            component: () => import(/* webpackChunkName: "dashboard" */ "@/dashboard/views/DCPOverview.vue"),
            props: true,
            meta: { remountOnParamChange: true, breadcrumb: districtLeafBreadcrumb("", [{ label: "DCP Overview" }]) },
          },
        ],
      },
      {
        path: "dashboard/district/:districtId/division/:divisionId",
        component: () =>
          import(/* webpackChunkName: "dashboard-layout" */ "@/dashboard/layouts/DashboardWorkspaceLayout.vue"),
        props: true,
        children: [
          {
            path: "",
            name: "DivisionDashboard",
            component: () => import(/* webpackChunkName: "dashboard" */ "@/dashboard/views/DivisionDashboard.vue"),
            props: true,
            meta: { public: true, remountOnParamChange: true },
          },
        ],
      },
      {
        path: "dashboard/district/:districtId/division/:divisionId/area/:areaId",
        component: () =>
          import(/* webpackChunkName: "dashboard-layout" */ "@/dashboard/layouts/DashboardWorkspaceLayout.vue"),
        props: true,
        children: [
          {
            path: "",
            name: "AreaDashboard",
            component: () => import(/* webpackChunkName: "dashboard" */ "@/dashboard/views/AreaDashboard.vue"),
            props: true,
            meta: { public: true, remountOnParamChange: true },
          },
        ],
      },
      // The Find District / Register District pages were retired. Redirect any
      // stale bookmarks or links to the replacement Find a Club landing rather
      // than letting them fall through to the catch-all → /app/.
      {
        path: "findDistrict",
        redirect: "/findaclub",
      },
      {
        path: "registerDistrict",
        redirect: "/findaclub",
      },
      // District management mainSite.controllers.mainsiteR.routes
      {
        path: "district/:districtId",
        component: () =>
          import(/* webpackChunkName: "dashboard-layout" */ "@/dashboard/layouts/DashboardWorkspaceLayout.vue"),
        props: true,
        children: [
          {
            path: "",
            name: "DistrictManagement",
            component: () => import(/* webpackChunkName: "district" */ "@/dashboard/views/DistrictManagementView.vue"),
            props: true,
          },
          {
            path: "config",
            name: "DistrictConfig",
            component: () => import(/* webpackChunkName: "district" */ "@/dashboard/views/DistrictConfigView.vue"),
            props: true,
            meta: { remountOnParamChange: true },
          },
          {
            path: "uploadData",
            name: "DistrictUploadData",
            component: () => import(/* webpackChunkName: "district" */ "@/dashboard/views/DistrictUploadView.vue"),
            props: true,
          },
          {
            path: "uploadComplete",
            name: "DistrictUploadComplete",
            component: () =>
              import(/* webpackChunkName: "district" */ "@/dashboard/views/DistrictUploadCompleteView.vue"),
            props: true,
            meta: { breadcrumb: districtLeafBreadcrumb("upload", [{ label: "Upload Complete" }]) },
          },
          {
            path: "mailingLists",
            name: "DistrictMailingLists",
            component: () => import(/* webpackChunkName: "district" */ "@/dashboard/views/DistrictMailingLists.vue"),
            props: true,
          },
          {
            path: "emailProvider",
            name: "DistrictEmailProvider",
            component: () =>
              import(/* webpackChunkName: "district" */ "@/dashboard/views/DistrictEmailProviderView.vue"),
            props: true,
          },
          {
            // District COT landing page. Public so the everybody-facing
            // calendar + register section renders without a login. Officers
            // reach `district-cot-management` from its Manage button.
            path: "cot",
            name: "district-cot",
            component: () =>
              import(/* webpackChunkName: "district" */ "@/dashboard/views/DistrictCOTManagementView.vue"),
            props: true,
            meta: { public: true },
          },
          {
            // Officer-facing COT event detail + register. Nested under the
            // district so the URL carries districtId — the workspace sidebar
            // then expands the owning district like the other district COT
            // pages (highlighting is route-driven). The `event` path segment is
            // static, so these never collide with the admin `cot/:cotId/...`
            // routes below.
            //
            // `remountOnParamChange` on both: each page is wholly about one
            // event and runs flows spanning a mutation and the refetch after
            // it, so reusing the instance under a second `:eventId` would leave
            // the first event's busy state, cancel dialog and success panel over
            // the second (#2822).
            path: "cot/event/:eventId",
            name: "cot-event-detail",
            component: () => import(/* webpackChunkName: "cot" */ "@/cot/views/COTEventDetailView.vue"),
            props: true,
            meta: {
              requiresAuth: true,
              remountOnParamChange: true,
              breadcrumb: cotLeafBreadcrumb([{ label: "Event" }]),
            },
          },
          {
            path: "cot/event/:eventId/register",
            name: "cot-event-register",
            component: () => import(/* webpackChunkName: "cot" */ "@/cot/views/COTRegisterView.vue"),
            props: true,
            meta: {
              requiresAuth: true,
              remountOnParamChange: true,
              breadcrumb: cotLeafBreadcrumb([{ label: "Register" }]),
            },
          },
          {
            // Officer tools for the district's COT: the rounds table and the
            // activity log, as tabs. The static segment ranks above `cot/:cotId`.
            path: "cot/management",
            name: "district-cot-management",
            component: () => import(/* webpackChunkName: "district" */ "@/dashboard/views/DistrictCOTManageView.vue"),
            props: true,
            meta: {
              requiresAuth: true,
              breadcrumb: cotLeafBreadcrumb([{ label: "Management" }]),
            },
          },
          {
            path: "cot/:cotId",
            name: "district-cot-detail",
            component: () => import(/* webpackChunkName: "district" */ "@/dashboard/views/DistrictCOTDetailView.vue"),
            props: true,
            meta: {
              remountOnParamChange: true,
              breadcrumb: cotLeafBreadcrumb([{ label: "Round" }]),
            },
          },
          {
            path: "cot/:cotId/events/:eventId",
            name: "district-cot-event-detail",
            component: () => import(/* webpackChunkName: "district" */ "@/dashboard/views/DistrictCOTEventView.vue"),
            props: true,
            meta: {
              remountOnParamChange: true,
              breadcrumb: cotLeafBreadcrumb([{ label: "Event" }]),
            },
          },
          {
            path: "cot/:cotId/events/:eventId/sessions/:sessionId",
            name: "district-cot-session-detail",
            component: () =>
              import(/* webpackChunkName: "district" */ "@/dashboard/views/DistrictCOTSessionDetailView.vue"),
            props: true,
            meta: {
              remountOnParamChange: true,
              breadcrumb: cotLeafBreadcrumb([{ label: "Session" }]),
            },
          },
          {
            path: "cot/:cotId/clubs",
            name: "district-cot-club-summary",
            component: () =>
              import(/* webpackChunkName: "district" */ "@/dashboard/views/DistrictCOTClubSummaryView.vue"),
            props: true,
            meta: { breadcrumb: cotLeafBreadcrumb([{ label: "Club Summary" }]) },
          },
          {
            path: "cot/:cotId/unmatched",
            name: "district-cot-unmatched",
            component: () =>
              import(/* webpackChunkName: "district" */ "@/dashboard/views/DistrictCOTUnmatchedView.vue"),
            props: true,
            meta: { breadcrumb: cotLeafBreadcrumb([{ label: "Unmatched Attendees" }]) },
          },
          {
            // Back-compat: redirect the old /cots and /cots/* URLs to /cot/*.
            path: "cots/:pathMatch(.*)*",
            redirect: (to) => {
              // For the exact `/cots` URL the repeatable pathMatch param is the
              // empty string, not an array — only `.join()` on the array form,
              // otherwise it would throw and the redirect would fail.
              const match = to.params.pathMatch;
              const rest = Array.isArray(match) ? match.join("/") : (match ?? "");
              const base = `/district/${to.params.districtId}/cot`;
              return rest ? `${base}/${rest}` : base;
            },
          },
        ],
      },

      // Officer-facing COT pages (Phase 1C — issue #1508).
      // The "My Officer Training" landing is now a collapsible section on the
      // home page (HomeCOTSection); keep a redirect so old /app/cot links work.
      {
        path: "cot",
        redirect: { name: "UserHome" },
      },

      // Phase 1D — public, unauthenticated registration URL for officers from
      // non-ToastHost districts (issue #1509). Logged-in officers hitting this
      // URL are routed by the view to the district-scoped officer form at
      // /app/district/:districtId/cot/event/:eventId/register.
      {
        path: "cot/register/:eventId",
        name: "cot-public-register",
        component: () => import(/* webpackChunkName: "cot" */ "@/cot/views/PublicCOTRegisterView.vue"),
        props: true,
        meta: { public: true, requiresCompleteProfileWhenAuthenticated: true },
      },

      // #1682 — public double opt-in landing. The verification email links here
      // with ?eventId & ?token; the view confirms the registration on mount.
      {
        path: "cot/verify",
        name: "cot-verify",
        component: () => import(/* webpackChunkName: "cot" */ "@/cot/views/COTVerifyRegistrationView.vue"),
        meta: { public: true },
      },

      // Admin mainSite.controllers.mainsiteR.routes
      {
        path: "admin",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminHomeView.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb() },
      },
      {
        path: "admin/browse",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminBrowseHomeView.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb([{ label: "Browse" }]) },
      },
      {
        path: "admin/browse/:type",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminBrowseView.vue"),
        props: true,
        meta: {
          requiresAdmin: true,
          breadcrumb: adminBreadcrumb((route) => [
            { label: "Browse", to: "/admin/browse" },
            { label: `Browse ${String(route.params.type)}` },
          ]),
        },
      },
      {
        path: "admin/browse/:type/:id",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminBrowseItemView.vue"),
        props: true,
        meta: {
          requiresAdmin: true,
          breadcrumb: adminBreadcrumb((route) => [
            { label: "Browse", to: "/admin/browse" },
            { label: `Browse ${String(route.params.type)}`, to: `/admin/browse/${String(route.params.type)}` },
            { label: "Details" },
          ]),
        },
      },
      {
        path: "admin/stats",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminStatsView.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb([{ label: "Stats" }]) },
      },
      {
        path: "admin/pathways",
        name: "AdminPathways",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminPathwaysView.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb([{ label: "Pathways" }]) },
      },
      {
        path: "admin/usage-analytics",
        name: "AdminUsageAnalytics",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminUsageAnalyticsView.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb([{ label: "Usage Analytics" }]) },
      },
      {
        path: "admin/email-log",
        name: "AdminEmailLog",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminEmailLogView.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb([{ label: "Email Log" }]) },
      },
      {
        path: "admin/nudges",
        name: "AdminNudges",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminNudgesView.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb([{ label: "Nudges" }]) },
      },
      {
        path: "admin/easyspeak-capture",
        name: "AdminEasySpeakCapture",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminEasySpeakCaptureView.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb([{ label: "EasySpeak Capture" }]) },
      },
      {
        path: "admin/easyspeak-identities",
        name: "AdminEasySpeakIdentityReview",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminEasySpeakIdentityReviewView.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb([{ label: "EasySpeak Identities" }]) },
      },
      {
        path: "admin/easyspeak-support",
        name: "AdminEasySpeakSupport",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminEasySpeakSupportView.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb([{ label: "EasySpeak Support" }]) },
      },
      {
        path: "admin/debug-repair",
        name: "AdminDebugRepair",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminDebugRepairView.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb([{ label: "Repair approval" }]) },
      },
      {
        path: "admin/testPayment",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminTestPayment.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb([{ label: "Test Payment" }]) },
      },
      {
        path: "admin/club-status",
        component: () => import(/* webpackChunkName: "admin" */ "@/admin/views/AdminClubStatusView.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb([{ label: "Club Status" }]) },
      },
      {
        path: "admin/clubs",
        component: () => import(/* webpackChunkName: "admin" */ "@/components/admin/SubscriptionManagement.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb([{ label: "Clubs" }]) },
      },
      {
        path: "admin/clubs/:clubId",
        component: () => import(/* webpackChunkName: "admin" */ "@/components/admin/SubscriptionDetails.vue"),
        props: true,
        meta: {
          requiresAdmin: true,
          breadcrumb: adminBreadcrumb([{ label: "Clubs", to: "/admin/clubs" }, { label: "Club Details" }]),
        },
      },
      {
        path: "admin/subscriptions",
        component: () => import(/* webpackChunkName: "admin" */ "@/components/admin/SubscriptionManagement.vue"),
        meta: { requiresAdmin: true, breadcrumb: adminBreadcrumb([{ label: "Clubs" }]) },
      },
      {
        path: "admin/subscriptions/:clubId",
        component: () => import(/* webpackChunkName: "admin" */ "@/components/admin/SubscriptionDetails.vue"),
        props: true,
        meta: {
          requiresAdmin: true,
          breadcrumb: adminBreadcrumb([{ label: "Clubs", to: "/admin/clubs" }, { label: "Club Details" }]),
        },
      },
    ],
  },
  {
    path: "/",
    component: ChatLayout,
    children: [
      {
        path: "chat/",
        component: () => import(/* webpackChunkName: "chat" */ "@/chat/views/ChatView.vue"),
        props: true,
        meta: { public: true },
      },
    ],
  },
  // Guest registration standalone mainSite.controllers.mainsiteR.routes with minimal layout
  {
    path: "/club/:clubShortName/guest/",
    component: GuestLayout,
    children: [
      {
        path: "register",
        name: "GuestRegistration",
        component: () => import(/* webpackChunkName: "guest" */ "@/club/views/GuestRegistration.vue"),
        props: true,
        meta: { public: true, remountOnParamChange: true },
      },
      {
        path: "registered",
        name: "GuestRegistered",
        component: () => import(/* webpackChunkName: "guest" */ "@/club/views/GuestRegistered.vue"),
        props: true,
        meta: { public: true, remountOnParamChange: true },
      },
    ],
  },
  {
    path: "/club/:clubShortName/no-meeting",
    name: "NoMeetingAvailable",
    component: () => import(/* webpackChunkName: "no-meeting" */ "@/club/views/NoMeetingAvailable.vue"),
    props: true,
    meta: { public: true, remountOnParamChange: true, remountAtAppShell: true },
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

/**
 * A query- or hash-only change: the same page being looked at differently.
 *
 * Several pages write the URL as the reader works — the membership tab strip
 * replaces `?tab=` on every switch, and filters do the same — and those
 * navigations must leave the page where it is. A change of `path` is a
 * different page.
 */
function isSamePageNavigation(to: { path: string }, from: { path: string }): boolean {
  return to.path === from.path;
}

/**
 * Where a navigation leaves the window scrolled.
 *
 * Vue Router leaves the scroll position alone unless a `scrollBehavior` is
 * given, and the browser only restores one on a real reload. So without this an
 * officer part-way down one club page arrived part-way down the next page they
 * picked from the workspace sidebar.
 *
 * Declaring the behaviour also takes anchor scrolling off the browser, which is
 * why `to.hash` is handled here rather than left alone as it is in the
 * focus-moving `afterEach` below.
 */
/** How long a restored position waits for its page to grow back. */
const SAVED_POSITION_WAIT_MS = 1000;

/**
 * Resolve once the document can hold a scroll position of `top`.
 *
 * Vue Router applies a restored position as soon as the navigation confirms,
 * and on this app that is before the arriving page's reads have come back. The
 * document is still a spinner's height at that moment, so the browser clamps
 * the position to whatever fits and the reader lands near the top of a page
 * that grows underneath them. Returning a promise is the hook for waiting.
 *
 * Bounded, because a page may legitimately never grow back — a meeting deleted
 * since, a read that failed. The reader then lands as far down as the page
 * allows, which is where they would have landed without the wait.
 *
 * `setTimeout` rather than `requestAnimationFrame`: a browser stops serving
 * frames to a hidden tab, and a restore that never resolves would leave the
 * navigation hanging.
 *
 * Reports whether the wait was still wanted when it ended. Vue Router does not
 * cancel a `scrollBehavior` promise when a later navigation starts, so a
 * restore left running past its own navigation would scroll whatever page is on
 * screen a second later to the offset of the page being left.
 */
function whenPageCanHold(top: number, isCurrent: () => boolean): Promise<boolean> {
  const deadline = Date.now() + SAVED_POSITION_WAIT_MS;
  return new Promise((resolve) => {
    const check = () => {
      if (!isCurrent()) {
        resolve(false);
        return;
      }
      if (document.documentElement.scrollHeight >= top + window.innerHeight || Date.now() >= deadline) {
        resolve(true);
        return;
      }
      setTimeout(check, 16);
    };
    check();
  });
}

/**
 * Which navigation owns the scroll.
 *
 * Only the restore arm waits, so only it can still be running when the next
 * navigation arrives — but the counter advances on every call, because the
 * navigation that supersedes a restore is usually an ordinary forward one whose
 * own answer is "go to the top".
 */
let scrollGeneration = 0;

export const scrollBehavior: RouterScrollBehavior = (to, from, savedPosition) => {
  const generation = ++scrollGeneration;
  // Back and forward return to where the reader was, hash or no hash — once the
  // page can hold the position, and only if that navigation is still the one on
  // screen.
  if (savedPosition) {
    return whenPageCanHold(savedPosition.top, () => generation === scrollGeneration).then((current) =>
      current ? savedPosition : false,
    );
  }
  if (to.hash) return { el: to.hash };
  if (isSamePageNavigation(to, from)) return false;
  return { top: 0 };
};

const router = createRouter({
  history: createWebHistory(APP_BASE_URL),
  routes,
  scrollBehavior,
});

function withChunkReloadBuster(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set(CHUNK_RELOAD_QUERY_PARAM, Date.now().toString());
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    // Fallback that preserves `#hash` while ensuring the query param stays in the real querystring.
    const [beforeHash, hash] = url.split("#", 2);
    const joiner = beforeHash.includes("?") ? "&" : "?";
    return `${beforeHash}${joiner}${CHUNK_RELOAD_QUERY_PARAM}=${Date.now()}${hash ? `#${hash}` : ""}`;
  }
}

/**
 * The URL the chunk-load recovery reload navigates to.
 *
 * This is a hard navigation the browser resolves against the origin, not a
 * router push, so it must be an **emitted** URL. `to.fullPath` is a route path
 * with the history base excluded — passed straight through, the reload would
 * request a path the server does not serve the SPA at, and the user would get a
 * non-SPA response instead of the retry. `window.location.pathname` in the
 * fallback is already emitted and must not be prefixed again.
 */
export function chunkRecoveryTarget(to?: { fullPath?: string } | null): string {
  return to?.fullPath ? appHref(to.fullPath) : window.location.pathname + window.location.search;
}

function hasChunkReloadQueryParam(): boolean {
  try {
    return new URL(window.location.href).searchParams.has(CHUNK_RELOAD_QUERY_PARAM);
  } catch {
    // Extremely defensive fallback.
    return window.location.search.includes(CHUNK_RELOAD_QUERY_PARAM);
  }
}

function hasChunkHardFailQueryParam(): boolean {
  try {
    return new URL(window.location.href).searchParams.has(CHUNK_HARD_FAIL_QUERY_PARAM);
  } catch {
    // Extremely defensive fallback.
    return window.location.search.includes(CHUNK_HARD_FAIL_QUERY_PARAM);
  }
}

function clearChunkLoadQueryParams(): void {
  try {
    const url = new URL(window.location.href);
    const hasReloadParam = url.searchParams.has(CHUNK_RELOAD_QUERY_PARAM);
    const hasHardFailParam = url.searchParams.has(CHUNK_HARD_FAIL_QUERY_PARAM);
    if (!hasReloadParam && !hasHardFailParam) return;

    if (hasReloadParam) url.searchParams.delete(CHUNK_RELOAD_QUERY_PARAM);
    if (hasHardFailParam) url.searchParams.delete(CHUNK_HARD_FAIL_QUERY_PARAM);
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  } catch {
    // ignore
  }
}

router.onError((error, to) => {
  const message = (error as Error)?.message ?? String(error);
  if (!CHUNK_LOAD_ERROR_RE.test(message)) {
    logger.error(error);
    return;
  }

  // Primary guard: URL params work even when sessionStorage is unavailable (e.g. Safari private mode).
  let hardFailed = hasChunkHardFailQueryParam();
  let alreadyReloaded = hasChunkReloadQueryParam();
  try {
    const reloadState = sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY);
    hardFailed = hardFailed || reloadState === "hardFailed";
    alreadyReloaded = alreadyReloaded || reloadState === "true" || reloadState === "hardFailed";
  } catch {
    // keep current value
  }

  if (hardFailed) {
    logger.error("Chunk load failed after hard-fail; not reloading.", { error, to });
    if (!hasShownChunkHardFailAlert) {
      hasShownChunkHardFailAlert = true;
      window.alert(
        "Toasthost couldn't load this page. It may have been updated, or your connection may be offline.\n\nPlease hard refresh (Ctrl+Shift+R / Cmd+Shift+R) and try again.",
      );
    }
    return;
  }

  if (alreadyReloaded) {
    logger.error(`Chunk load failed after reload; redirecting to ${appHref("/")} (hard-fail).`, { error, to });
    try {
      sessionStorage.setItem(CHUNK_LOAD_RELOAD_KEY, "hardFailed");
    } catch {
      // ignore
    }

    window.alert(
      "Toasthost couldn't load this page. It may have been updated, or your connection may be offline.\n\nPlease hard refresh (Ctrl+Shift+R / Cmd+Shift+R) and try again.",
    );

    try {
      const url = new URL(appHref("/"), window.location.origin);
      url.searchParams.set(CHUNK_HARD_FAIL_QUERY_PARAM, "1");
      window.location.assign(url.pathname + url.search + url.hash);
    } catch {
      window.location.assign(`${appHref("/")}?${CHUNK_HARD_FAIL_QUERY_PARAM}=1`);
    }
    return;
  }

  try {
    sessionStorage.setItem(CHUNK_LOAD_RELOAD_KEY, "true");
  } catch {
    // ignore
  }

  window.location.assign(withChunkReloadBuster(chunkRecoveryTarget(to)));
});

router
  .isReady()
  .then(() => {
    try {
      sessionStorage.removeItem(CHUNK_LOAD_RELOAD_KEY);
    } catch {
      // ignore
    }

    clearChunkLoadQueryParams();
  })
  .catch(() => {
    // ignore
  });

type RouteMetaKey = "public" | "requiresAuth" | "requiresAdmin";
type MatchedRouteMeta = Pick<RouteRecordRaw, "meta">;

function hasMatchedMetaFlag(matched: readonly MatchedRouteMeta[], metaKey: RouteMetaKey): boolean {
  return matched.some((record) => record.meta?.[metaKey] === true);
}

export function deriveAccessMeta(matched: readonly MatchedRouteMeta[]) {
  const requiresAuth = hasMatchedMetaFlag(matched, "requiresAuth");

  return {
    public: !requiresAuth && hasMatchedMetaFlag(matched, "public"),
    requiresAdmin: hasMatchedMetaFlag(matched, "requiresAdmin"),
  };
}

// Apply metadata to mainSite.controllers.mainsiteR.routes
router.beforeEach((to, from) => {
  const accessMeta = deriveAccessMeta(to.matched);
  to.meta.public = accessMeta.public;
  to.meta.requiresAdmin = accessMeta.requiresAdmin;

  // Apply auth guard for protected mainSite.controllers.mainsiteR.routes
  return authGuard(to, from);
});

// Accessible navigation: move focus to the page heading after SPA route changes
router.afterEach((to, from) => {
  // An anchor navigation (e.g. /profile#password) puts the reader at the anchor,
  // which `scrollBehavior` above does; moving focus to the heading would undo it.
  if (to.hash) return;
  if (isSamePageNavigation(to, from)) return;

  nextTick(() => {
    requestAnimationFrame(() => {
      // Only focus if the page has registered a heading via usePageHeadingFocus.
      // Non-pilot pages (e.g. meeting views) don't register headings and
      // focusing #app-main-content as a fallback can trigger window focus
      // handlers that interfere with page state (e.g. membership revalidation).
      focusRegisteredPageHeading({ preventScroll: true });
    });
  });
});

export default router;
